import { Outlet } from '@tanstack/react-router';

import { useEffect, useState } from 'react';
import { usePathname } from '@/lib/router-compat';
import Link from '@/components/ui/link-compat';
import { useAuth } from '@/contexts/AuthContext';
import { Settings } from 'lucide-react';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { useMediaQuery } from '@/hooks/use-media-query';
import { navGroups } from '@/components/settings/MobileSettingsIndex';

function isActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(href + '/');
}

function SettingsLayout() {
  const pathname = usePathname();
  const { isSuperAdmin } = useAuth();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [optimisticPath, setOptimisticPath] = useState<string | null>(null);

  useEffect(() => {
    setOptimisticPath(null);
  }, [pathname]);

  const activePath = optimisticPath ?? pathname;

  const visibleGroups = navGroups.filter((g) => !g.adminOnly || isSuperAdmin);

  // Find the current nav item label for breadcrumb
  const allItems = navGroups.flatMap((g) => g.items);
  const currentItem = allItems.find((item) => isActive(pathname, item.href));

  return (
    <div className="flex flex-col md:flex-row gap-6 flex-1 min-h-0">
      {currentItem && (
        <div className="md:hidden mb-2">
          <Breadcrumb
            items={[
              { label: "Settings", href: "/dashboard/settings" },
              { label: currentItem.label },
            ]}
          />
        </div>
      )}

      {/* Nav hidden on mobile — the TopBar back button and the grouped index cover navigation. */}
      <aside
        aria-label="Settings navigation"
        className={isDesktop ? 'w-64 shrink-0' : 'hidden'}
      >
        {/* Desktop sidebar */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 px-3 py-2 mb-2">
            <Settings className="h-5 w-5" />
            <h2 className="text-lg font-semibold">Settings</h2>
          </div>
          {visibleGroups.map((group, gi) => (
            <div key={group.title} className={gi > 0 ? 'mt-6' : ''}>
              <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {group.title}
              </p>
              {group.items.map((item) => {
                const active = isActive(activePath, item.href);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    aria-current={active ? 'page' : undefined}
                    onPointerDown={() => setOptimisticPath(item.href)}
                    className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      active
                        ? 'bg-accent text-accent-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                    }`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/settings')({ component: SettingsLayout });
